import { z } from 'zod';
import { careAppointmentModalitySchema } from '../care/appointments';
import {
  careAppointmentAttendanceOutcomeSchema,
  telemedicineAdhocCallOutcomeSchema,
} from './telemedicine';
import { apiEnvelope } from '../envelope';
import { internalRoleSchema } from '../identity/schema';

// ─── vocabularies ────────────────────────────────────────────────────────────
//
// Closed sets shared by the telemedicine_ai_notes table CHECKs, the wire, the
// pipeline and the analytics dimensions. Extending one is an additive enum
// change here first; the CHECKs derive from these arrays.

/** A member's AI notes setting, or a counsellor's mid-call change for one call. */
export const TELEMEDICINE_AI_NOTES_DECISION = ['on', 'off'] as const;
export const telemedicineAiNotesDecisionSchema = z.enum(TELEMEDICINE_AI_NOTES_DECISION);
export type TelemedicineAiNotesDecision = z.infer<typeof telemedicineAiNotesDecisionSchema>;

/**
 * Where a call's effective decision came from: its own mid-call change, the
 * member's setting, or the default when nobody has set one.
 */
export const TELEMEDICINE_AI_NOTES_DECISION_SOURCE = ['call', 'member', 'default'] as const;
export const telemedicineAiNotesDecisionSourceSchema = z.enum(
  TELEMEDICINE_AI_NOTES_DECISION_SOURCE,
);
export type TelemedicineAiNotesDecisionSource = z.infer<
  typeof telemedicineAiNotesDecisionSourceSchema
>;

/**
 * Workflow state of one recorded appointment. `pending` from capture start,
 * `processing` while transcription runs, then one terminal state. Every
 * terminal state but `failed` keeps the audio: `recorded` (AI notes Off for
 * the whole call), `withdrawn` (AI notes turned Off during the call) and
 * `recording_stopped` (the member's recording was switched Off mid-call) are
 * never transcribed.
 */
export const TELEMEDICINE_AI_NOTES_STATUS = [
  'pending',
  'processing',
  'note_created',
  'failed',
  'withdrawn',
  'recorded',
  'recording_stopped',
] as const;
export const telemedicineAiNotesStatusSchema = z.enum(TELEMEDICINE_AI_NOTES_STATUS);
export type TelemedicineAiNotesStatus = z.infer<typeof telemedicineAiNotesStatusSchema>;

export const TELEMEDICINE_AI_NOTES_TERMINAL_STATUS = [
  'note_created',
  'failed',
  'withdrawn',
  'recorded',
  'recording_stopped',
] as const satisfies readonly TelemedicineAiNotesStatus[];
export type TelemedicineAiNotesTerminalStatus =
  (typeof TELEMEDICINE_AI_NOTES_TERMINAL_STATUS)[number];

/** Why a note was not produced. Bounded so it is safe as a metric tag. */
export const TELEMEDICINE_AI_NOTES_FAILURE_REASON = [
  'capture_failed',
  'scribe_failed',
  'empty_note',
  'no_healthie_chart',
  'healthie_form_missing',
  'healthie_rejected',
  'healthie_unavailable',
] as const;
export const telemedicineAiNotesFailureReasonSchema = z.enum(TELEMEDICINE_AI_NOTES_FAILURE_REASON);
export type TelemedicineAiNotesFailureReason = z.infer<
  typeof telemedicineAiNotesFailureReasonSchema
>;

/** The speaker roles a normalised transcript segment is attributed to. */
export const TELEMEDICINE_TRANSCRIPT_SPEAKER = ['counsellor', 'member'] as const;
export const telemedicineTranscriptSpeakerSchema = z.enum(TELEMEDICINE_TRANSCRIPT_SPEAKER);
export type TelemedicineTranscriptSpeaker = z.infer<typeof telemedicineTranscriptSpeakerSchema>;

// Client-parsed responses read status through a catch so a status added later
// renders as unknown rather than failing the whole payload (ADR-135).
const clientStatusSchema = telemedicineAiNotesStatusSchema.or(z.string().min(1));

// ─── decision routes (telemedicine) ──────────────────────────────────────────

export const telemedicineAiNotesDecisionRequestSchema = z
  .object({ decision: telemedicineAiNotesDecisionSchema })
  .strict();
export type TelemedicineAiNotesDecisionRequest = z.infer<
  typeof telemedicineAiNotesDecisionRequestSchema
>;

export const telemedicineAiNotesStateDataSchema = z.object({
  appointmentId: z.string().uuid(),
  /** False when the environment runs the pipeline disabled; nothing is shown. */
  available: z.boolean(),
  /** False when the member's call recording is Off and this call was not captured. */
  recorded: z.boolean().default(true),
  /** The call's effective decision. */
  decision: telemedicineAiNotesDecisionSchema.nullable(),
  source: telemedicineAiNotesDecisionSourceSchema.or(z.string().min(1)).nullable().default(null),
  status: clientStatusSchema.nullable(),
});
export type TelemedicineAiNotesStateData = z.infer<typeof telemedicineAiNotesStateDataSchema>;

export const telemedicineAiNotesStateResponseSchema = apiEnvelope(
  telemedicineAiNotesStateDataSchema,
);
export type TelemedicineAiNotesStateResponse = z.infer<
  typeof telemedicineAiNotesStateResponseSchema
>;

// ─── member setting routes (internal-members) ────────────────────────────────

export const memberAiNotesSettingSchema = z.object({
  enabled: z.boolean(),
  /** `default` until staff record a setting for the member. */
  source: z.enum(['member', 'default']).or(z.string().min(1)),
  recordedAt: z.string().datetime({ offset: true }).nullable(),
});
export type MemberAiNotesSetting = z.infer<typeof memberAiNotesSettingSchema>;

export const memberAiNotesSettingResponseSchema = apiEnvelope(memberAiNotesSettingSchema);
export type MemberAiNotesSettingResponse = z.infer<typeof memberAiNotesSettingResponseSchema>;

export const memberAiNotesSettingWriteRequestSchema = z.object({ enabled: z.boolean() }).strict();
export type MemberAiNotesSettingWriteRequest = z.infer<
  typeof memberAiNotesSettingWriteRequestSchema
>;

export const memberCallRecordingSettingSchema = z.object({
  enabled: z.boolean(),
  /** `default` until staff record a setting, or Pelago 1.0's was imported. */
  source: z.enum(['member', 'default']).or(z.string().min(1)),
  recordedAt: z.string().datetime({ offset: true }).nullable(),
  /** One of the member's calls is being recorded right now. */
  recordingInProgress: z.boolean().default(false),
});
export type MemberCallRecordingSetting = z.infer<typeof memberCallRecordingSettingSchema>;

export const memberCallRecordingSettingResponseSchema = apiEnvelope(
  memberCallRecordingSettingSchema,
);
export type MemberCallRecordingSettingResponse = z.infer<
  typeof memberCallRecordingSettingResponseSchema
>;

export const memberCallRecordingSettingWriteRequestSchema = z
  .object({ enabled: z.boolean() })
  .strict();
export type MemberCallRecordingSettingWriteRequest = z.infer<
  typeof memberCallRecordingSettingWriteRequestSchema
>;

// ─── history routes (internal-members) ───────────────────────────────────────

export const telemedicineHistoryVideoEntrySchema = z.object({
  kind: z.literal('video').default('video'),
  appointmentId: z.string().uuid(),
  startsAt: z.string().datetime({ offset: true }),
  durationMinutes: z.number().int().positive(),
  counsellorName: z.string().nullable(),
  attendanceOutcome: careAppointmentAttendanceOutcomeSchema.or(z.string().min(1)).nullable(),
  aiNotesDecision: telemedicineAiNotesDecisionSchema.or(z.string().min(1)).nullable(),
  aiNotesStatus: clientStatusSchema.nullable(),
  hasTranscript: z.boolean(),
  /** The Healthie charting note (form answer group) id, once the note exists. */
  healthieNoteId: z.string().min(1).nullable().default(null),
  /** Visit modality when stored; absent reads as video in clients. */
  modality: careAppointmentModalitySchema.or(z.string().min(1)).optional(),
  /** A completed adhoc call placed against this appointment, folded into the row. */
  linkedCall: z
    .object({
      outcome: telemedicineAdhocCallOutcomeSchema.or(z.string().min(1)),
      durationSeconds: z.number().int().nonnegative(),
    })
    .optional(),
});
export type TelemedicineHistoryVideoEntry = z.infer<typeof telemedicineHistoryVideoEntrySchema>;

export const telemedicineHistoryPhoneEntrySchema = z.object({
  kind: z.literal('phone'),
  callId: z.string().uuid(),
  startsAt: z.string().datetime({ offset: true }),
  counsellorName: z.string().nullable(),
  outcome: telemedicineAdhocCallOutcomeSchema.or(z.string().min(1)),
  durationSeconds: z.number().int().nonnegative(),
  outcomeConfirmedByName: z.string().nullable(),
  /** Who placed the call; absent from an API older than the attribution columns. */
  placedByName: z.string().nullable().optional(),
  /** The placer's org role at call time; null on rows written before it was captured. */
  placedByRole: internalRoleSchema.or(z.string().min(1)).nullable().optional(),
});
export type TelemedicineHistoryPhoneEntry = z.infer<typeof telemedicineHistoryPhoneEntrySchema>;

/** Video and completed unscheduled phone calls in one staff history timeline. */
export const telemedicineHistoryEntrySchema = z.union([
  telemedicineHistoryPhoneEntrySchema,
  telemedicineHistoryVideoEntrySchema,
]);
export type TelemedicineHistoryEntry = z.infer<typeof telemedicineHistoryEntrySchema>;

export const telemedicineHistoryDataSchema = z.object({
  calls: z.array(telemedicineHistoryEntrySchema),
});
export type TelemedicineHistoryData = z.infer<typeof telemedicineHistoryDataSchema>;

export const telemedicineHistoryResponseSchema = apiEnvelope(telemedicineHistoryDataSchema);
export type TelemedicineHistoryResponse = z.infer<typeof telemedicineHistoryResponseSchema>;

export const telemedicineTranscriptSegmentSchema = z.object({
  speaker: telemedicineTranscriptSpeakerSchema.or(z.string().min(1)),
  text: z.string(),
  startMs: z.number().int().nonnegative(),
  endMs: z.number().int().nonnegative(),
});
export type TelemedicineTranscriptSegment = z.infer<typeof telemedicineTranscriptSegmentSchema>;

/** The stored, normalised transcript document (also the transcript read payload). */
export const telemedicineTranscriptSchema = z.object({
  appointmentId: z.string().uuid(),
  segments: z.array(telemedicineTranscriptSegmentSchema),
});
export type TelemedicineTranscript = z.infer<typeof telemedicineTranscriptSchema>;

export const telemedicineTranscriptResponseSchema = apiEnvelope(telemedicineTranscriptSchema);
export type TelemedicineTranscriptResponse = z.infer<typeof telemedicineTranscriptResponseSchema>;
